import { projectConstructionLines } from "../Project";
import {
  deriveConstructionOffsets,
  isOffsetDerived,
  reachableOffsets,
  resolveOffsetRoot,
} from "../constructionOffset";
import { OFFSET_TOOL_ICON_SVG } from "../../icons/offsetTool";
import type {
  Connection,
  ConnectionChipSpec,
  ConnectionRowSpec,
} from "./Connection";

/** Issue #180 follow-up: the offset façade for the Connections
 *  registry. All real work still lives in
 *  `src/model/constructionOffset.ts` — this shim exposes the same
 *  helpers through the `Connection` interface so the panel
 *  loops + commit pipeline can iterate one list. */

const KIND = "offset" as const;

function pairKey(sourceId: string, derivedId: string): string {
  return `${sourceId}->${derivedId}`;
}

export const offsetConnection: Connection = {
  kind: KIND,
  // Runs after snap pruning, before mirror — a mirror-of-offset
  // depends on the offset's already-derived pathData.
  order: 1,

  chipsForLine(line, _project, ctx) {
    const chips: ConnectionChipSpec[] = [];
    if (line.offsetSource) {
      chips.push({
        kind: KIND,
        connectionKey: pairKey(line.offsetSource.sourceId, line.id),
        role: "derived",
        iconSvg: OFFSET_TOOL_ICON_SVG,
        ariaLabel: "Offset of source",
        title: "Offset of source",
        className: "offset-chip",
        hoverRefs: [
          { kind: "line", constructionId: line.offsetSource.sourceId },
        ],
        clickTarget: { constructionId: line.offsetSource.sourceId },
        extraDatasetEntries: [
          ["offsetPair", pairKey(line.offsetSource.sourceId, line.id)],
          ["offsetRole", "derived"],
        ],
      });
    }
    for (const other of ctx.allLines) {
      if (other.offsetSource?.sourceId === line.id) {
        chips.push({
          kind: KIND,
          connectionKey: pairKey(line.id, other.id),
          role: "source",
          iconSvg: OFFSET_TOOL_ICON_SVG,
          ariaLabel: "Has offset",
          title: "Has offset",
          className: "offset-chip",
          hoverRefs: [{ kind: "line", constructionId: other.id }],
          clickTarget: { constructionId: other.id },
          extraDatasetEntries: [
            ["offsetPair", pairKey(line.id, other.id)],
            ["offsetRole", "source"],
          ],
        });
      }
    }
    return chips;
  },

  rowsForLine(line, _project, ctx) {
    const rows: ConnectionRowSpec[] = [];
    if (line.offsetSource) {
      const src = ctx.allLines.find(
        (l) => l.id === line.offsetSource!.sourceId,
      );
      if (src) {
        const partnerName =
          src.name ?? `Construction ${ctx.allLines.indexOf(src) + 1}`;
        const derivedId = line.id;
        rows.push({
          kind: KIND,
          connectionKey: pairKey(src.id, line.id),
          role: "derived",
          iconSvg: OFFSET_TOOL_ICON_SVG,
          ariaLabel: `Offset of ${partnerName}`,
          title: `Offset of ${partnerName}`,
          className: "offset-chip",
          hoverRefs: [{ kind: "line", constructionId: src.id }],
          clickTarget: { constructionId: src.id },
          extraDatasetEntries: [
            ["offsetPair", pairKey(src.id, line.id)],
            ["offsetRole", "derived"],
          ],
          label: `Offset of ${partnerName}`,
          onDetach: (editor) => {
            editor.commit("Delete offset", (proj) => ({
              ...proj,
              constructionLines: (proj.constructionLines ?? []).map((l) => {
                if (l.id !== derivedId) return l;
                const { offsetSource: _drop, ...rest } = l;
                return rest;
              }),
            }));
          },
        });
      }
    }
    for (const other of ctx.allLines) {
      if (other.offsetSource?.sourceId === line.id) {
        const partnerName =
          other.name ?? `Construction ${ctx.allLines.indexOf(other) + 1}`;
        const derivedId = other.id;
        rows.push({
          kind: KIND,
          connectionKey: pairKey(line.id, other.id),
          role: "source",
          iconSvg: OFFSET_TOOL_ICON_SVG,
          ariaLabel: `Offset → ${partnerName}`,
          title: `Offset → ${partnerName}`,
          className: "offset-chip",
          hoverRefs: [{ kind: "line", constructionId: other.id }],
          clickTarget: { constructionId: other.id },
          extraDatasetEntries: [
            ["offsetPair", pairKey(line.id, other.id)],
            ["offsetRole", "source"],
          ],
          label: `Offset → ${partnerName}`,
          onDetach: (editor) => {
            editor.commit("Delete offset", (proj) => ({
              ...proj,
              constructionLines: (proj.constructionLines ?? []).map((l) => {
                if (l.id !== derivedId) return l;
                const { offsetSource: _drop, ...rest } = l;
                return rest;
              }),
            }));
          },
        });
      }
    }
    return rows;
  },

  derive({ project, scope }) {
    return deriveConstructionOffsets(project, scope);
  },

  pruneForDeletedLines({ project, deletedLineIds }) {
    if (deletedLineIds.size === 0) return project;
    const lines = projectConstructionLines(project);
    if (lines.length === 0) return project;
    let changed = false;
    const next = lines.filter((l) => {
      if (!l.offsetSource) return true;
      if (deletedLineIds.has(l.offsetSource.sourceId)) {
        changed = true;
        return false;
      }
      return true;
    });
    return changed ? { ...project, constructionLines: next } : project;
  },

  rederiveLive({ scene, changedId }) {
    // Delegate to the existing Scene helper — it already handles
    // the transitive offset-of-offset chain via its own recursion.
    scene.rederiveOffsetsOf(changedId);
  },

  forwardClosure({ project, originIds }) {
    return reachableOffsets(project, originIds);
  },

  resolveRoot(project, lineId) {
    return resolveOffsetRoot(project, lineId);
  },

  isDerived(line) {
    return isOffsetDerived(line);
  },

  detectCycles(project) {
    // Walk every line's offsetSource chain; mark any line that
    // loops back to itself. Defensive — `deriveConstructionOffsets`
    // already caps depth at 1000, but stripping cycles up-front
    // keeps the deriver cleaner.
    const cycled = new Set<string>();
    const lines = projectConstructionLines(project);
    const byId = new Map(lines.map((l) => [l.id, l] as const));
    for (const line of lines) {
      if (!line.offsetSource) continue;
      const seen = new Set<string>();
      let cur = line.offsetSource.sourceId;
      let steps = 0;
      while (cur && steps < 1000) {
        if (cur === line.id) {
          cycled.add(line.id);
          break;
        }
        if (seen.has(cur)) break; // cycle that doesn't include line
        seen.add(cur);
        const nxt = byId.get(cur);
        if (!nxt?.offsetSource) break;
        cur = nxt.offsetSource.sourceId;
        steps++;
      }
    }
    return cycled;
  },

  stripBackRefs(project, lineIds) {
    if (lineIds.size === 0) return project;
    const lines = projectConstructionLines(project);
    let changed = false;
    const next = lines.map((l) => {
      if (!lineIds.has(l.id) || !l.offsetSource) return l;
      changed = true;
      const { offsetSource: _drop, ...rest } = l;
      return rest;
    });
    return changed ? { ...project, constructionLines: next } : project;
  },
};
